/**
 * Date utility functions to handle various date formats safely
 */

/**
 * Safely parse a date string that might be in various formats
 * @param {string|number|Date} dateValue - The date value to parse
 * @returns {Date|null} - A valid Date object or null if parsing fails
 */
export function safeParseDate(dateValue) {
  // Return null for empty or undefined values
  if (!dateValue) {
    return null;
  }

  // If it's already a Date object, return it
  if (dateValue instanceof Date) {
    return isValidDate(dateValue) ? dateValue : null;
  }

  // If it's a number (timestamp), convert to Date
  if (typeof dateValue === 'number') {
    const date = new Date(dateValue * 1000); // Assume Unix timestamp in seconds
    return isValidDate(date) ? date : new Date(dateValue); // Try milliseconds if seconds failed
  }

  // If it's a string, try various parsing methods
  if (typeof dateValue === 'string') {
    // First, try the standard Date constructor for ISO formats
    let date = new Date(dateValue);
    if (isValidDate(date)) {
      return date;
    }

    // Try to parse DD/MM/YYYY format
    const ddmmyyyyMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddmmyyyyMatch) {
      const [, day, month, year] = ddmmyyyyMatch;
      date = new Date(year, month - 1, day); // Month is 0-indexed in JS
      if (isValidDate(date)) {
        return date;
      }
    }

    // Try to parse DD/MM/YY format (2-digit year)
    const ddmmyyMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
    if (ddmmyyMatch) {
      const [, day, month, year] = ddmmyyMatch;
      // Convert 2-digit year to 4-digit year
      // Assume years 00-29 are 2000-2029, and years 30-99 are 1930-1999
      const fullYear = parseInt(year) <= 29 ? 2000 + parseInt(year) : 1900 + parseInt(year);
      date = new Date(fullYear, month - 1, day); // Month is 0-indexed in JS
      if (isValidDate(date)) {
        return date;
      }
    }

    // Try to parse DD-MM-YYYY format
    const ddmmyyyyDashMatch = dateValue.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (ddmmyyyyDashMatch) {
      const [, day, month, year] = ddmmyyyyDashMatch;
      date = new Date(year, month - 1, day);
      if (isValidDate(date)) {
        return date;
      }
    }

    // Try to parse YYYY-MM-DD format (ISO date)
    const yyyymmddMatch = dateValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (yyyymmddMatch) {
      const [, year, month, day] = yyyymmddMatch;
      date = new Date(year, month - 1, day);
      if (isValidDate(date)) {
        return date;
      }
    }

    // Try parsing as Unix timestamp (string number)
    const timestamp = parseInt(dateValue, 10);
    if (!isNaN(timestamp)) {
      date = new Date(timestamp * 1000); // Try seconds first
      if (isValidDate(date) && date.getFullYear() > 1970) {
        return date;
      }
      
      date = new Date(timestamp); // Try milliseconds
      if (isValidDate(date) && date.getFullYear() > 1970) {
        return date;
      }
    }
  }

  // If all parsing attempts fail, return null
  console.warn('Failed to parse date:', dateValue);
  return null;
}

/**
 * Check if a Date object is valid
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is valid
 */
export function isValidDate(date) {
  return date instanceof Date && !isNaN(date.getTime());
}

/**
 * Format a date to YYYY-MM-DD format for API consumption
 * @param {Date} date - The date to format
 * @returns {string} - The formatted date string
 */
export function formatDateForAPI(date) {
  if (!isValidDate(date)) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Format a date to DD/MM/YYYY format for display
 * @param {Date} date - The date to format
 * @returns {string} - The formatted date string
 */
export function formatDateForDisplay(date) {
  if (!isValidDate(date)) {
    return '';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year}`;
}

/**
 * Get today's date
 * @returns {Date} - Today's date
 */
export function getToday() {
  return new Date();
}

/**
 * Add days to a date
 * @param {Date} date - The base date
 * @param {number} days - Number of days to add
 * @returns {Date} - The new date
 */
export function addDays(date, days) {
  if (!isValidDate(date)) {
    return null;
  }
  
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}
